/**
 * Career Domain Explorer - Landing Page
 * Dual-path: Browse domains OR take personality quiz
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getDomains, getProfile, getSessionId } from '../../api/career-api';
import type { CareerDomain } from '../../types';
import { Header } from '../../../../shared/components/Header';
import { Footer } from '../../../../shared/components/Footer';

const ExploreLandingPage: React.FC = () => {
  console.log('🎯 Component function called');

  const [domains, setDomains] = useState<CareerDomain[]>(() => {
    console.log('🎯 Initializing domains state to empty array');
    return [];
  });
  const [isLoading, setIsLoading] = useState(() => {
    console.log('🎯 Initializing isLoading state to false');
    return false;
  });
  const [hasProfile, setHasProfile] = useState(false);
  const [showMatchPrompt, setShowMatchPrompt] = useState(false);

  useEffect(() => {
    console.log('🎯 useEffect triggered - component mounted');
    console.log('🎯 About to call loadData()');
    loadData();
  }, []);

  const loadData = async () => {
    console.log('🎯 loadData() START');
    try {
      console.log('🎯 Setting isLoading to TRUE');
      setIsLoading(true);
      console.log('🎯 isLoading set to true');

      console.log('🎯 About to call getDomains()');
      const domainsData = await getDomains();
      console.log('🎯 getDomains() returned:', domainsData);
      console.log('🎯 Number of domains:', domainsData.length);

      console.log('🎯 About to call setDomains()');
      setDomains(domainsData);
      console.log('🎯 setDomains() called');

      // Check if user has a profile (don't let this block rendering)
      try {
        console.log('🎯 Getting session ID');
        const sessionId = getSessionId();
        console.log('🎯 Session ID:', sessionId);

        console.log('🎯 Fetching profile');
        const profile = await getProfile(sessionId);
        console.log('🎯 Profile:', profile);
        setHasProfile(!!profile);

        // Show match prompt after 3 seconds if no profile
        if (!profile) {
          setTimeout(() => setShowMatchPrompt(true), 3000);
        }
      } catch (profileError) {
        console.log('🎯 Profile error (expected):', profileError);
        setHasProfile(false);
      }
    } catch (error) {
      console.error('🎯 ❌ ERROR in loadData:', error);
    } finally {
      console.log('🎯 FINALLY block - setting isLoading to FALSE');
      setIsLoading(false);
      console.log('🎯 loadData() COMPLETE');
    }
  };

  console.log('🎯 RENDER - isLoading:', isLoading, '| domains.length:', domains.length, '| hasProfile:', hasProfile);

  if (isLoading) {
    console.log('🎯 ⏳ RENDERING LOADING SPINNER (isLoading is TRUE)');
    return (
      <>
        <Header />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading career domains...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  console.log('🎯 ✅ RENDERING MAIN CONTENT with', domains.length, 'domains');

  return (
    <>
      <Header />
      <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary via-emerald-600 to-teal-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-md px-4 py-2 rounded-full mb-6 border border-white/30">
              <span className="text-2xl">🎯</span>
              <span className="text-sm font-bold uppercase tracking-wide">Career Discovery</span>
            </div>

            <h1 className="text-5xl md:text-6xl font-extrabold mb-6 leading-tight">
              Discover Your Perfect Career Path
            </h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto mb-8 leading-relaxed">
              Explore 10 career domains, 33 specializations, and get AI-powered recommendations tailored to your unique profile.
            </p>

            {/* Stats Pills */}
            <div className="flex flex-wrap justify-center gap-4">
              <div className="bg-white/10 backdrop-blur-md border border-white/20 px-6 py-3 rounded-full">
                <span className="font-bold text-2xl">10</span>
                <span className="text-white/80 ml-2">Domains</span>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/20 px-6 py-3 rounded-full">
                <span className="font-bold text-2xl">33</span>
                <span className="text-white/80 ml-2">Specializations</span>
              </div>
              <div className="bg-white/10 backdrop-blur-md border border-white/20 px-6 py-3 rounded-full">
                <span className="font-bold text-2xl">∞</span>
                <span className="text-white/80 ml-2">Possibilities</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dual Path Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 mb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Path 1: Browse Freely */}
          <div className="bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 border-2 border-emerald-200 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all hover:scale-[1.02] relative overflow-hidden group">
            {/* Animated background gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-100/50 via-teal-100/50 to-cyan-100/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

            <div className="relative z-10">
              <div className="text-center mb-6">
                {/* Icon with gradient background */}
                <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  <span className="text-4xl">🔍</span>
                </div>

                <h2 className="text-2xl font-bold mb-3 bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                  Browse All Domains
                </h2>

                <p className="text-gray-700 mb-4">
                  Explore 10 career domains and 33 specializations at your own pace. No commitment required.
                </p>

                {/* Feature highlights */}
                <div className="flex flex-col gap-2 mb-6 text-sm text-left">
                  <div className="flex items-center gap-2 text-gray-600">
                    <span className="text-emerald-500 font-bold">✓</span>
                    <span>Browse all 10 career domains</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <span className="text-emerald-500 font-bold">✓</span>
                    <span>Salary insights & growth trends</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <span className="text-emerald-500 font-bold">✓</span>
                    <span>Detailed specialization breakdowns</span>
                  </div>
                </div>
              </div>

              <a
                href="#domains"
                className="block w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-center py-4 px-6 rounded-xl font-bold transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                📚 Start Browsing
              </a>
            </div>
          </div>

          {/* Path 2: Get Matched */}
          <div className="bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 border-2 border-purple-200 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all hover:scale-[1.02] relative overflow-hidden group">
            {/* Animated background gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-100/50 via-indigo-100/50 to-blue-100/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

            {!hasProfile && (
              <div className="absolute top-3 right-3 bg-gradient-to-r from-amber-400 to-orange-400 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-md animate-pulse">
                ⭐ RECOMMENDED
              </div>
            )}

            <div className="relative z-10">
              <div className="text-center mb-6">
                {/* Icon with gradient background */}
                <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                  <span className="text-4xl">✨</span>
                </div>

                <h2 className="text-2xl font-bold mb-3 bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                  Get Personalized Matches
                </h2>

                <p className="text-gray-700 mb-4">
                  Take our AI-powered quiz to discover careers that align with your interests, skills, and goals.
                </p>

                {/* Feature highlights */}
                <div className="flex flex-col gap-2 mb-6 text-sm text-left">
                  <div className="flex items-center gap-2 text-gray-600">
                    <span className="text-green-500 font-bold">✓</span>
                    <span>4-minute personality assessment</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <span className="text-green-500 font-bold">✓</span>
                    <span>Personalized career recommendations</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <span className="text-green-500 font-bold">✓</span>
                    <span>Match score for each domain</span>
                  </div>
                </div>
              </div>

              <Link
                to="/explore/careers/match"
                className="block w-full bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 hover:from-purple-700 hover:via-indigo-700 hover:to-blue-700 text-white text-center py-4 px-6 rounded-xl font-bold transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                {hasProfile ? '🎯 View My Matches' : '🚀 Take Career Quiz'}
              </Link>
            </div>
          </div>
        </div>

        {/* Progressive Prompt */}
        {showMatchPrompt && !hasProfile && (
          <div className="mt-6 bg-yellow-50 border-2 border-yellow-200 rounded-xl p-6 flex items-start gap-4 animate-fade-in">
            <div className="text-3xl">💡</div>
            <div className="flex-1">
              <h3 className="font-bold text-yellow-900 mb-2">Not sure where to start?</h3>
              <p className="text-yellow-800 mb-3">
                Take our quick personality quiz to get personalized domain recommendations
                based on your interests, work style, and priorities!
              </p>
              <Link
                to="/explore/careers/match"
                className="inline-block bg-yellow-400 hover:bg-yellow-500 text-yellow-900 px-4 py-2 rounded-lg font-semibold text-sm transition-colors"
              >
                Get Personalized Matches
              </Link>
            </div>
            <button
              onClick={() => setShowMatchPrompt(false)}
              className="text-yellow-600 hover:text-yellow-800 text-xl leading-none"
            >
              ×
            </button>
          </div>
        )}
      </div>

      {/* All Domains Section */}
      <div id="domains" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-100 to-teal-100 dark:from-emerald-950 dark:to-teal-950 px-4 py-2 rounded-full mb-4">
            <span className="text-lg">💼</span>
            <span className="text-sm font-bold text-emerald-700 dark:text-emerald-300 uppercase tracking-wide">All Career Domains</span>
          </div>
          <h2 className="text-4xl font-bold mb-4 bg-gradient-to-r from-primary to-emerald-600 bg-clip-text text-transparent">
            Browse {domains.length} Career Domains
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Deep dive into any domain to discover specializations, salary insights, growth trends, and required skills
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {domains.map((domain) => (
            <Link
              key={domain.id}
              to={`/explore/careers/${domain.slug}`}
              className="group bg-gradient-to-br from-card to-card/50 border-2 border-border hover:border-primary rounded-2xl p-6 transition-all hover:shadow-xl hover:scale-[1.02] relative overflow-hidden"
            >
              {/* Hover gradient background */}
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-50/50 to-teal-50/50 dark:from-emerald-950/20 dark:to-teal-950/20 opacity-0 group-hover:opacity-100 transition-opacity"></div>

              <div className="relative z-10">
                {/* Icon & Name */}
                <div className="flex items-start gap-4 mb-4">
                  <div className="text-5xl p-3 bg-gradient-to-br from-emerald-100 to-teal-100 dark:from-emerald-900 dark:to-teal-900 rounded-xl group-hover:scale-110 transition-transform">
                    {domain.icon_emoji || '📚'}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold mb-2 group-hover:text-primary transition-colors">
                      {domain.name}
                    </h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {domain.short_description}
                    </p>
                  </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 gap-3 pt-4 border-t border-border/50">
                  {domain.avg_salary_usd && (
                    <div className="bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950/30 dark:to-green-950/30 rounded-lg p-3">
                      <div className="text-xs text-muted-foreground mb-1 font-medium">💰 Avg Salary</div>
                      <div className="font-bold text-emerald-700 dark:text-emerald-400 text-lg">
                        ${(domain.avg_salary_usd / 1000).toFixed(0)}k
                      </div>
                    </div>
                  )}
                  {domain.job_growth_percent && (
                    <div className="bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-950/30 dark:to-cyan-950/30 rounded-lg p-3">
                      <div className="text-xs text-muted-foreground mb-1 font-medium">📈 Growth</div>
                      <div className="font-bold text-teal-700 dark:text-teal-400 text-lg">
                        +{domain.job_growth_percent}%
                      </div>
                    </div>
                  )}
                  {domain._count && (
                    <div className="col-span-2 bg-gradient-to-br from-primary/10 to-emerald-50 dark:from-primary/20 dark:to-emerald-950/30 rounded-lg p-3">
                      <div className="text-xs text-muted-foreground mb-1 font-medium">🎯 Specializations</div>
                      <div className="font-bold text-primary dark:text-emerald-400 text-lg">
                        {domain._count.specializations} career paths
                      </div>
                    </div>
                  )}
                </div>

                {/* CTA Arrow */}
                <div className="mt-4 pt-4 border-t border-border/50 text-primary font-bold text-sm flex items-center justify-between group-hover:text-emerald-700 transition-colors">
                  <span>Explore Domain</span>
                  <span className="text-xl group-hover:translate-x-1 transition-transform">→</span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {domains.length === 0 && (
          <div className="text-center py-20">
            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-emerald-100 to-teal-100 dark:from-emerald-900 dark:to-teal-900 rounded-full flex items-center justify-center">
              <span className="text-5xl">🔍</span>
            </div>
            <h3 className="text-2xl font-bold mb-3 text-foreground">No domains available yet</h3>
            <p className="text-lg text-muted-foreground">Check back soon for exciting career opportunities!</p>
          </div>
        )}
      </div>
    </div>
    <Footer />
    </>
  );
};

export default ExploreLandingPage;
